/**
 * 1–N identity marks as one overlapped cluster.
 *
 * The cluster is ONE object to assistive technology — a single combined label —
 * because "United, Lufthansa, +2" read out as four separate images is noise,
 * and the marks are a single fact about the itinerary rather than four facts.
 * The overlap is a negative inline margin so the cluster stays intrinsic-width
 * and never reserves a column.
 */
"use client";
import type { ReactElement } from "react";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import {
  CRC_MARK_PIXELS,
  CRC_TEXT_MUTED,
  CRC_TILE_BACKGROUND_COLOR,
} from "./cresultcard.tokens";
import {
  CresultcardMarkShape,
  CresultcardMarkSize,
} from "./cresultcard.types";
import { CresultcardIdentityMark } from "./CresultcardIdentityMark";

const OVERLAP = -6;
const OVERFLOW_PREFIX = "+";
const LABEL_SEPARATOR = ", ";
const OVERFLOW_SIZE_RATIO = 0.32;

export interface CresultcardMarkSpec {
  readonly name: string;
  readonly src?: string;
}

interface CresultcardMarkStackProps {
  readonly marks: readonly CresultcardMarkSpec[];
  readonly maxVisible: number;
  readonly size?: CresultcardMarkSize;
  readonly shape?: CresultcardMarkShape;
}

export function CresultcardMarkStack({
  marks,
  maxVisible,
  size = CresultcardMarkSize.Row,
  shape = CresultcardMarkShape.Square,
}: CresultcardMarkStackProps): ReactElement {
  const radius = useCresultcardNestedRadius();
  const visible = marks.slice(0, maxVisible);
  const overflow = marks.length - visible.length;
  const pixels = CRC_MARK_PIXELS[size];

  return (
    <span
      aria-label={marks.map((mark) => mark.name).join(LABEL_SEPARATOR)}
      data-el="Cresultcard/MarkStack/Root"
      role="img"
      style={{
        display: "inline-flex",
        alignItems: "center",
        flex: "0 0 auto",
        minWidth: pixels,
      }}
    >
      {visible.map((mark, index) => (
        <span
          aria-hidden
          key={mark.name}
          style={{
            display: "inline-flex",
            marginInlineStart: index === 0 ? 0 : OVERLAP,
          }}
        >
          <CresultcardIdentityMark
            name={mark.name}
            shape={shape}
            size={size}
            src={mark.src}
          />
        </span>
      ))}
      {overflow > 0 && (
        <span
          aria-hidden
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: pixels,
            height: pixels,
            marginInlineStart: OVERLAP,
            borderRadius:
              shape === CresultcardMarkShape.Circle ? "50%" : radius,
            fontSize: Math.round(pixels * OVERFLOW_SIZE_RATIO),
            fontWeight: 760,
            lineHeight: 1,
            color: CRC_TEXT_MUTED,
            backgroundColor: CRC_TILE_BACKGROUND_COLOR,
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.09)",
          }}
        >
          {`${OVERFLOW_PREFIX}${String(overflow)}`}
        </span>
      )}
    </span>
  );
}
